/**
 * ============================================================
 * KEJJA OS — WelcomePage
 * 5-slide intro shown once after first home creation.
 * ============================================================
 */

import { useState } from 'react';
import {
  Wallet, Package, Utensils, Repeat, Calendar, Users2,
  Sparkles, Lightbulb, CloudSun, DoorOpen, Camera, Music, Wind,
  ArrowRight, ArrowLeft, Check, Home as HomeIcon,
} from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

const SLIDES = 5;

export default function WelcomePage() {
  const { user } = useAuth();
  const [idx, setIdx] = useState(0);

  const completeMut = useMutation({
    mutationFn: () => api.post<{ ok: true }>('/me/complete-welcome', {}),
    onSettled: () => {
      // Prevent WelcomeGate loop even before user state refreshes
      try { if (user?.id) sessionStorage.setItem(`kejja_welcomed_${user.id}`, '1'); } catch { /* ignore */ }
      // Hard navigate so auth state fully re-fetches
      window.location.href = '/dashboard';
    },
  });

  const next = () => {
    if (idx < SLIDES - 1) setIdx(idx + 1);
    else completeMut.mutate();
  };

  const firstName = user?.displayName?.split(' ')[0] ?? 'friend';

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#0e415e] via-[#196287] to-[#1a4f75] text-white flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">

        {/* Progress dots */}
        <div className="flex gap-1.5 mb-8">
          {Array.from({ length: SLIDES }).map((_, i) => (
            <div key={i} className={cn(
              'h-1.5 rounded-full transition-all',
              i === idx ? 'w-8 bg-white' : i < idx ? 'w-1.5 bg-white/60' : 'w-1.5 bg-white/20',
            )} />
          ))}
        </div>

        <div className="w-full max-w-2xl">
          {idx === 0 && <SlideIntro firstName={firstName} />}
          {idx === 1 && <SlideModules />}
          {idx === 2 && <SlideAI />}
          {idx === 3 && <SlideHomeAuto />}
          {idx === 4 && <SlideReady />}
        </div>
      </div>

      {/* Footer controls */}
      <div className="px-6 pb-8 pt-4 border-t border-white/10">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          {idx > 0 ? (
            <button onClick={() => setIdx(idx - 1)}
              className="h-11 px-4 rounded-lg border border-white/20 text-white/80 hover:bg-white/10 hover:text-white text-sm font-medium flex items-center gap-1.5 transition">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          ) : (
            <button onClick={() => completeMut.mutate()}
              className="h-11 px-4 text-white/50 hover:text-white text-xs font-medium">
              Skip
            </button>
          )}
          <button onClick={next}
            disabled={completeMut.isPending}
            className="h-11 px-6 bg-white text-[#0e415e] font-bold rounded-lg hover:scale-105 transition-transform text-sm flex items-center gap-2 shadow-lg disabled:opacity-70">
            {idx === SLIDES - 1 ? (
              completeMut.isPending ? 'Opening dashboard…' : <><Check className="w-4 h-4" /> Enter dashboard</>
            ) : (
              <>Next <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
        </div>
      </div>
    </main>
  );
}

// ============================================================
// Slides
// ============================================================

function SlideIntro({ firstName }: { firstName: string }) {
  return (
    <div className="text-center">
      <div className="h-20 w-20 rounded-2xl bg-white/10 backdrop-blur border-2 border-white/30 flex items-center justify-center mx-auto mb-6">
        <HomeIcon className="w-10 h-10 text-white" strokeWidth={2} />
      </div>
      <p className="text-sky-200 text-xs uppercase tracking-[0.3em] font-semibold mb-3">
        Welcome home
      </p>
      <h1 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight">Hi {firstName},<br/>this is Kejja.</h1>
      <p className="text-white/85 text-base md:text-lg max-w-lg mx-auto leading-relaxed">
        One app for everything that matters at home — money, meals, pantry, routines, calendar, and community.
      </p>
      <p className="text-white/60 text-sm mt-4 max-w-lg mx-auto italic">
        Built in Kenya, for African households. 🇰🇪
      </p>
    </div>
  );
}

function SlideModules() {
  const modules = [
    { icon: Wallet,   label: 'Finance',   sub: 'M-Pesa, budgets, bills, Sacco',     color: 'from-orange-400 to-rose-500' },
    { icon: Package,  label: 'Pantry',    sub: 'Stock, expiry, low-on-hand',        color: 'from-emerald-400 to-teal-500' },
    { icon: Utensils, label: 'Meals',     sub: 'Plans, recipes, shopping list',     color: 'from-lime-400 to-emerald-500' },
    { icon: Repeat,   label: 'Routines',  sub: 'Chores, streaks, check-ins',        color: 'from-sky-400 to-blue-500' },
    { icon: Calendar, label: 'Calendar',  sub: 'Family events, reminders',          color: 'from-indigo-400 to-violet-500' },
    { icon: Users2,   label: 'Community', sub: 'Chamas, fellowships, couples',      color: 'from-rose-400 to-pink-500' },
  ];
  return (
    <div>
      <div className="text-center mb-6">
        <p className="text-sky-200 text-xs uppercase tracking-[0.3em] font-semibold mb-2">What's inside</p>
        <h2 className="text-3xl md:text-4xl font-extrabold">Six pillars of home life</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {modules.map((m) => (
          <div key={m.label} className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-4 hover:bg-white/15 transition">
            <div className={cn("h-10 w-10 rounded-lg bg-gradient-to-br flex items-center justify-center mb-3", m.color)}>
              <m.icon className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="text-sm font-bold">{m.label}</div>
            <div className="text-[11px] text-white/70 mt-0.5">{m.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideAI() {
  const features = [
    { icon: Sparkles, label: 'Budget insights', sub: '"You spent 38% more on fuel this month."' },
    { icon: Utensils, label: 'Recipe ideas',     sub: 'What to cook from what\'s in your pantry right now.' },
    { icon: Wallet,   label: 'Smart triage',     sub: 'Auto-categorize messy M-Pesa statements.' },
    { icon: CloudSun, label: 'Daily brief',      sub: 'Your morning family briefing, in your language.' },
  ];
  return (
    <div>
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-1.5 bg-amber-400/20 border border-amber-300/40 rounded-full px-3 py-1 mb-3">
          <Sparkles className="w-3 h-3 text-amber-200" />
          <span className="text-[10px] font-bold text-amber-100 uppercase tracking-widest">Coming soon</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-extrabold">Meet Kejja AI</h2>
        <p className="text-white/85 text-sm mt-2 max-w-lg mx-auto">Your family's financial and household co-pilot.</p>
      </div>
      <div className="space-y-2">
        {features.map((f) => (
          <div key={f.label} className="bg-white/10 backdrop-blur border border-white/20 rounded-lg p-3 flex items-start gap-3">
            <div className="h-8 w-8 rounded-md bg-amber-400/20 text-amber-200 flex items-center justify-center flex-shrink-0">
              <f.icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold">{f.label}</div>
              <div className="text-[11px] text-white/75 italic mt-0.5">{f.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideHomeAuto() {
  const devices = [
    { icon: Lightbulb, label: 'Lights' },
    { icon: Wind,      label: 'Curtains' },
    { icon: DoorOpen,  label: 'Doors' },
    { icon: Camera,    label: 'CCTV' },
    { icon: Music,     label: 'Audio' },
    { icon: CloudSun,  label: 'Climate' },
  ];
  return (
    <div>
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-1.5 bg-violet-400/20 border border-violet-300/40 rounded-full px-3 py-1 mb-3">
          <Sparkles className="w-3 h-3 text-violet-200" />
          <span className="text-[10px] font-bold text-violet-100 uppercase tracking-widest">The vision</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-extrabold">Your whole home, one command centre</h2>
        <p className="text-white/85 text-sm mt-2 max-w-lg mx-auto">
          Kejja will one day run your physical home too — plug in lights, cameras, doors, and more.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {devices.map((d) => (
          <div key={d.label} className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-4 flex flex-col items-center text-center">
            <div className="h-11 w-11 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 flex items-center justify-center mb-2 shadow-lg">
              <d.icon className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="text-xs font-semibold">{d.label}</div>
          </div>
        ))}
      </div>
      <p className="text-center text-[11px] text-white/50 mt-5 italic">
        Build it crazy, build it well.
      </p>
    </div>
  );
}

function SlideReady() {
  return (
    <div className="text-center">
      <div className="h-20 w-20 rounded-2xl bg-emerald-400/20 border-2 border-emerald-300/40 flex items-center justify-center mx-auto mb-6">
        <Check className="w-10 h-10 text-emerald-200" strokeWidth={2.5} />
      </div>
      <h2 className="text-3xl md:text-5xl font-extrabold mb-3">You're all set.</h2>
      <p className="text-white/85 text-base md:text-lg max-w-lg mx-auto mb-6">
        Start small — add your first M-Pesa transaction, stock a pantry item, or invite a family member.
      </p>
      <div className="inline-flex flex-col gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-xl px-5 py-4 text-left">
        <div className="text-[10px] font-bold text-white/60 uppercase tracking-widest">Quick start</div>
        <div className="text-sm flex items-center gap-2"><span className="text-amber-300">1.</span> Forward an M-Pesa SMS into Finance</div>
        <div className="text-sm flex items-center gap-2"><span className="text-amber-300">2.</span> Add one pantry item</div>
        <div className="text-sm flex items-center gap-2"><span className="text-amber-300">3.</span> Invite your spouse or a parent</div>
      </div>
    </div>
  );
}
